import { Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Snackbar, Typography } from "@mui/material";
import { useState } from "react";
import StripeService from "../../services/StripeService";
import { IPlan } from "../../types/Plan";
import IAlert from "../../types/Alert";
import LogService from "../../services/LogService";
import { useNavigate } from "react-router-dom";
import ILog from "../../types/Log";
import * as constantes from "../../common/Constantes";
import { useStripe } from "@stripe/react-stripe-js";

interface Props {
  planes: IPlan[];
  closeModal: () => void;
  onSuccess: () => void;
};

const PlanChangeForm = ({ planes, closeModal, onSuccess }: Props) => {
    const history = useNavigate();
    const stripe = useStripe();
    
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [updatingPlan, setUpdatingPlan] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const handleConfirm = () => {
      setConfirmOpen(true);
    };
  
    const handleCancelConfirm = () => {
      setConfirmOpen(false);
    };
  
    const handleAcceptConfirm = async () => {
      setConfirmOpen(false);
      await updatePlan();
    };

    const updatePlan = async () => {
        try {
        // Antes de cambiar el plan: sin Stripe cargado no se podría completar el 3D Secure y el cambio quedaría pendiente
        if (!stripe) {
          setAlert({ open: true, severity: 'error', msg: 'Stripe no está inicializado. Recarga la página.' });
          return;
        }

        setUpdatingPlan(true);
      
        const response = await StripeService.changePlan(selectedPlan);
        const { clientSecret, confirmed } = response.data; 

        if (confirmed) {
          closeModal();
          onSuccess();
          return;
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);

        if (error) {
          const errorMsg = error.message || 'Error en la autenticación 3D Secure.';
          const errorLog: ILog = { method: 'PlanChangeForm.Component.updatePlan', level: 'error', message: errorMsg, meta: error };
          LogService.logError(errorLog);
          setAlert({ open: true, severity: 'error', msg: errorMsg });
          return;
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          closeModal();
          onSuccess();
          return;
        }

        // Sin error ni éxito (antes la pantalla no decía nada). Si el pago no se completa, el webhook devuelve la suscripción al plan anterior
        setAlert({ open: true, severity: 'warning', msg: 'No se ha completado el pago del cambio de plan. Si no se completa, tu suscripción seguirá en el plan anterior y te avisaremos por email.' });
      
      } catch (err: any) {
        if (err.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          // Un 402 es un cobro rechazado por el banco y un 400, lo que hay que quitar antes de bajar de plan: no son fallos
          // de la app y no se registran
          if (err.response?.status !== 402 && err.response?.status !== 400) {
            const error: ILog = { method: 'PlanChangeForm.Component.updatePlan', level: 'error', message: err.message, meta: err.response };
            LogService.logError(error);
          }
          // El servidor responde con { message } (p. ej. 402: cobro del cambio de plan rechazado); los errores de Stripe traen raw.message
          setAlert({ open: true, severity: 'error', msg: err.response?.data?.message || err.response?.data?.raw?.message || err.raw?.message || 'No se ha podido cambiar el plan' });
        }
      } finally {
        setUpdatingPlan(false);
      }
    };

    const selectedPlanData = planes.find((p) => p.priceId === selectedPlan);

    return (
        <>
            <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mt: 1, pb: 2 }} >
                {planes.map((plan: IPlan) => {
                  const isSelected = selectedPlan === plan.priceId;
                
                  return (
                    <Grid item xs={12} sm={6} md={4} key={plan.priceId}>
                      <Card onClick={() => setSelectedPlan(plan.priceId)}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          textAlign: 'center',
                          cursor: 'pointer',
                          borderRadius: 3,
                          boxShadow: isSelected ? 6 : 2,
                          border: isSelected
                            ? '2px solid #63ad3c'
                            : '1px solid rgba(0,0,0,0.1)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.04)',
                            boxShadow: 6,
                            borderColor: '#63ad3c',
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 'bold',
                              color: isSelected ? '#63ad3c' : 'inherit',
                              mb: 1,
                            }}
                          >
                            {plan.name}
                          </Typography>
                        
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              mb: 1,
                            }}
                          >
                            {plan.price}
                          </Typography>
                        
                          {plan.limit !== null && (
                            <Typography variant="body2" color="text.secondary">
                              Hasta {plan.limit} usuarios activos
                            </Typography>
                          )}
                          {plan.limit === null && (
                            <Typography variant="body2" color="text.secondary">
                              Sin límite de usuarios
                            </Typography>
                          )}
                        </CardContent>
                      
                        <Box
                          sx={{
                            bgcolor: isSelected ? '#63ad3c' : 'transparent',
                            color: isSelected ? '#fff' : '#63ad3c',
                            py: 1,
                            fontWeight: 'bold',
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                          }}
                        >
                          {isSelected ? 'Seleccionado' : 'Seleccionar'}
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button color="error" onClick={closeModal}>Cancelar</Button>
              <Button
                variant="contained"
                color="success"
                disabled={!selectedPlan || updatingPlan}
                onClick={handleConfirm}
              >
                {updatingPlan ? <CircularProgress size={24} color="inherit" /> : 'Confirmar cambio'}
              </Button>
            </DialogActions>

            {/* Diálogo de confirmación */}
            <Dialog open={confirmOpen} onClose={handleCancelConfirm}>
              <DialogTitle>Confirmar cambio de plan</DialogTitle>
              <DialogContent>
                {selectedPlanData ? (
                  <>
                    <DialogContentText sx={{ mb: 2 }}>
                      ¿Estás seguro de que deseas cambiar tu plan actual por:
                    </DialogContentText>
                    <Box sx={{ textAlign: "center", mb: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 0.5 }}
                      >
                        {selectedPlanData.name}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedPlanData.price}
                      </Typography>
                    </Box>
                    <DialogContentText>
                      Este cambio se aplicará inmediatamente.
                    </DialogContentText>
                  </>
                ) : (
                  <DialogContentText>
                    ¿Estás seguro de que deseas cambiar tu plan actual? Este cambio se aplicará inmediatamente.
                  </DialogContentText>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelConfirm} color="error">
                  Cancelar
                </Button>
                <Button onClick={handleAcceptConfirm} autoFocus sx={{ color: "#63ad3c" }}>
                  Confirmar
                </Button>
              </DialogActions>
            </Dialog>

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
              <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>
        </>
    );

};

export default PlanChangeForm;